import React, { useEffect, useMemo, useState } from "react";
import { Card, CardHeader, CardBody } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import { getPaymentsRegionCategoryDistribution } from "common/services/payments.service";
import useQueryState from "Components/Hooks/UseQueryState";
import moment from "moment";
import { FilterType, defaultFilter } from "../Filters";
import useChartColors from "Components/Common/useChartColors";

const convertToSeries = (response: any[]) => {
  // Step 1: Extract all unique categories
  const categories = Array.from(
    new Set(
      response.flatMap((item) =>
        item.payments.map((payment: any) => payment.category)
      )
    )
  );

  // Step 2: Map each category to its totalCount values for each federalMemberState
  const countSeries = categories.map((category) => {
    return {
      name: category,
      data: response.map((item) => {
        const payment = item.payments.find(
          (payment: any) => payment.category === category
        );
        return payment ? payment.totalCount : 0; // Default to 0 if no payment for the category
      }),
    };
  });
  const amountSeries = categories.map((category) => {
    return {
      name: category,
      data: response.map((item) => {
        const payment = item.payments.find(
          (payment: any) => payment.category === category
        );
        return payment ? payment.totalAmount : 0; // Default to 0 if no payment for the category
      }),
    };
  });

  return { countSeries, amountSeries };
};

const AmountDistributionChart = ({
  series,
  categories,
}: {
  series: any[];
  categories: string[];
}) => {
  const chartColumnStacked100Colors = useChartColors(
    "region_category_distribution"
  );

  const options: any = {
    chart: {
      stacked: !0,
      //   stackType: "100%",
      toolbar: {
        show: !1,
      },
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          legend: {
            position: "bottom",
            offsetX: -10,
            offsetY: 0,
          },
        },
      },
    ],
    xaxis: {
      categories,
    },
    yaxis: {
      labels: {
        formatter: function (value: number) {
          if (value >= 1000000) {
            return (value / 1000000).toFixed(1) + "M"; // Convert to millions
          } else if (value >= 1000) {
            return (value / 1000).toFixed(1) + "K"; // Convert to thousands
          }
          return value.toString(); // Return the original value for smaller numbers
        },
      },
    },
    fill: {
      opacity: 1,
    },
    legend: {
      position: "right",
      offsetX: 0,
      offsetY: 50,
    },
    colors: chartColumnStacked100Colors,
    tooltip: {
      y: {
        formatter: function (value: number) {
          if (value >= 1000000) {
            return (value / 1000000).toFixed(1) + "M"; // Convert to millions
          } else if (value >= 1000) {
            return (value / 1000).toFixed(1) + "K"; // Convert to thousands
          }
          return value.toString(); // Return the original value for smaller numbers
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (value: number) {
        if (value >= 1000000) {
          return (value / 1000000).toFixed(1) + "M"; // Convert to millions
        } else if (value >= 1000) {
          return (value / 1000).toFixed(1) + "K"; // Convert to thousands
        }
        return value.toString(); // Return the original value for smaller numbers
      },
    },
  };

  return (
    <ReactApexChart
      dir="ltr"
      className="apex-charts"
      series={series}
      options={options}
      type="bar"
      height={350}
    />
  );
};

const ContractDistributionChart = ({
  series,
  categories,
}: {
  series: any[];
  categories: string[];
}) => {
  const chartColumnStacked100Colors = useChartColors(
    "region_category_distribution"
  );

  const options: any = {
    chart: {
      stacked: !0,
      //   stackType: "100%",
      toolbar: {
        show: !1,
      },
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          legend: {
            position: "bottom",
            offsetX: -10,
            offsetY: 0,
          },
        },
      },
    ],
    xaxis: {
      categories,
    },
    yaxis: {
      labels: {
        formatter: function (value: number) {
          return value.toLocaleString(); // Format numbers with commas only
        },
      },
    },
    fill: {
      opacity: 1,
    },
    legend: {
      position: "right",
      offsetX: 0,
      offsetY: 50,
    },
    colors: chartColumnStacked100Colors,
    tooltip: {
      y: {
        formatter: function (val: any) {
          return val.toLocaleString() + " Contracts";
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val: any) {
        return val.toLocaleString();
      },
    },
  };

  return (
    <ReactApexChart
      dir="ltr"
      className="apex-charts"
      series={series}
      options={options}
      type="bar"
      height={350}
    />
  );
};

const RegionCategoryDistribution = () => {
  const [filter] = useQueryState<FilterType>("filter", defaultFilter);

  const [payments, setPayments] = useState([]);
  const [, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      try {
        const response: any = await getPaymentsRegionCategoryDistribution({
          mdaId: filter?.mda?.value || undefined,
          regionId: filter?.region?.value || undefined,
          positionTypeId: filter?.positionType?.value || undefined,
          agencyId: filter?.agency?.value || undefined,
          paymentCategoryId: filter?.paymentCategory?.value || undefined,
          thematicAreaId: filter?.thematicArea?.value || undefined,
          sectorId: filter?.sector?.value || undefined,
          startDate: filter?.dateRange?.fromDate
            ? moment(filter.dateRange.fromDate)
                .toDate()
                .toLocaleDateString("en-CA")
            : undefined,
          endDate: filter?.dateRange?.toDate
            ? moment(filter.dateRange.toDate)
                .toDate()
                .toLocaleDateString("en-CA")
            : undefined,
        });

        setPayments(response);
      } catch (error) {
        console.warn("Error fetching total  support", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    filter?.agency?.value,
    filter?.dateRange?.fromDate,
    filter?.dateRange?.toDate,
    filter.mda,
    filter?.positionType?.value,
    filter?.region?.value,
    filter?.sector?.value,
    filter?.thematicArea?.value,
    filter?.paymentCategory?.value,
  ]);

  const { countSeries, amountSeries } = React.useMemo(
    () => convertToSeries(payments),
    [payments]
  );
  const categories = useMemo(() => {
    return payments.map((payment: any) => payment.federalMemberState);
  }, [payments]);

  const [activeTab, setActiveTab] = useState("number");

  return (
    <Card>
      <CardHeader className="d-flex align-items-center ">
        <div className="flex-grow-1">
          <h4 className="card-title mb-0">
            Contract Distribution by Region and Payment Category
          </h4>
        </div>
        <div className="d-flex gap-1">
          <button
            type="button"
            className={`btn btn-sm material-shadow-none ${
              activeTab === "number" ? "btn-soft-primary" : "btn-soft-secondary"
            }`}
            onClick={() => setActiveTab("number")}
          >
            NUMBER
          </button>
          <button
            type="button"
            className={`btn btn-sm material-shadow-none ${
              activeTab === "amount" ? "btn-soft-primary" : "btn-soft-secondary"
            }`}
            onClick={() => setActiveTab("amount")}
          >
            AMOUNT
          </button>
        </div>
      </CardHeader>
      <CardBody>
        {activeTab === "number" ? (
          <ContractDistributionChart
            series={countSeries}
            categories={categories}
          />
        ) : (
          <AmountDistributionChart
            series={amountSeries}
            categories={categories}
          />
        )}
      </CardBody>
    </Card>
  );
};

export default RegionCategoryDistribution;
